import { _setFixedWidth, GetRowIdParams, GridOptions, GridReadyEvent, RowSelectedEvent } from "@ag-grid-community/core";
import { Component, OnInit, Optional, ViewEncapsulation } from "@angular/core";

import { ActivatedRoute } from "@angular/router";
import { ItemDetailSummary } from "app/model/entities/-item-detail-summary";
import {
  BarcodePrintStatusEnum,
  Feature,
  ItemDetail,
  ItemDetailStatus,
  ItemDetailStatusEnum,
  ItemReplacement,
  Product,
  ProductType,
} from "app/model/entities/entity-model";
import { AgFns, GridOptionsExt, ISortModel, RowSelectionMultipleCheckbox } from "app/shared/ag-fns";
import { BarcodeService } from "app/shared/barcode.service";
import { DialogBaseComponent } from "app/shared/dialog-base.component";
import { DomainBaseComponent, wrapBusy } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import { NavFns } from "app/shared/nav-fns";
import { takeUntil } from "rxjs/operators";
import { InventoryDamageDialogComponent } from "./inventory-damage-dialog.component";
import { UniAgFns } from "@/shared/uni-ag-fns";
import { InventoryManagerComponent } from "./inventory-manager.component";

@Component({
  selector: "inventory",
  templateUrl: "./inventory.component.html",

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class InventoryComponent extends DomainBaseComponent implements OnInit {
  // @ViewChild("damageCell") damageCell: TemplateRef<any>;

  idsGridOptions: GridOptionsExt;
  itdGridOptions: GridOptionsExt;
  itemDetails: ItemDetail[];
  allItdStatuses: ItemDetailStatus[];
  selectedItdStatuses: ItemDetailStatus[];
  isAccountOwned: boolean;
  features: Feature[];
  showMarkLabel = "View Damaged";
  hasItdSelectedRows = false;

  constructor(
    protected domainService: DomainService,
    protected barcodeService: BarcodeService,
    protected route: ActivatedRoute,
    @Optional() private inventoryManager: InventoryManagerComponent
  ) {
    super(domainService);

    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });

    // Subscribe to print barcodes command from parent button
    this.inventoryManager?.printBarcodesClicked$.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.printBarcodes();
    });

    // Show the Print Barcodes button when this page is active (deferred to avoid ExpressionChangedAfterItHasBeenCheckedError)
    if (this.inventoryManager) {
      setTimeout(() => {
        // Reset other pages' controls
        this.inventoryManager.showEoqButtons = false;
        this.inventoryManager.showAddInventoryButtons = false;
        this.inventoryManager.showScannerControls = false;
        // Show this page's controls
        this.inventoryManager.showPrintBarcodesButton = true;
      });
    }
  }

  override ngOnDestroy() {
    // Hide the Print Barcodes button when leaving this page
    if (this.inventoryManager) {
      this.inventoryManager.showPrintBarcodesButton = false;
      this.inventoryManager.canPrintBarcodes = false;
    }
    super.ngOnDestroy();
  }

  async updateFromContext() {
    this.idsGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onIdsGridReady,
      getRowId: this.getRowIdForIds,
      onRowSelected: this.onIdsRowSelected,
      onFilterChanged: this.onFilterChanged,
      rowModelType: "serverSide",
    });
    this.idsGridOptions.onPaginationChanged = (params) => {
      if (params.newPage) {
        this.itemDetails = [];
        this.updateLocation();
      }
    };
    // TODO: 'id' in next line may not be right.
    AgFns.captureGridRouteParams(this.idsGridOptions, this.route, "idsKey");

    this.itdGridOptions = AgFns.initGridOptions(
      this,
      {
        onGridReady: this.onItdGridReady,
        onRowSelected: this.onItdRowSelected,
        rowSelection: { ...RowSelectionMultipleCheckbox, headerCheckbox: true },
      },
      { detailProperty: "itemDetailAddons" }
    );
    this.updateItdMasterDetail(this.itdGridOptions);

    this.features = await this.dbQueryService.getFeatures();
    this.allItdStatuses = this.dbQueryService.getAllCached(ItemDetailStatus).filter((ids) => ids.id != ItemDetailStatusEnum.Shipped);
    let statusIds: number[];
    if (this.idsGridOptions.context?.gridState?.fm == null) {
      statusIds = [ItemDetailStatusEnum.InInventory];
      this.selectedItdStatuses = this.allItdStatuses.filter((its) => statusIds.indexOf(its.id) !== -1);
    }

    this.isPageReady = true;
  }

  getRowIdForIds(params: GetRowIdParams) {
    let ids = params.data as ItemDetailSummary;
    return this.getKeyForIds(ids);
  }

  getKeyForIds(ids: ItemDetailSummary) {
    if (ids == null) {
      return "";
    }
    return ids.productId.toString() + "-" + ids.itemDetailStatusId.toString() + "-" + (ids.accountId ?? "").toString();
  }

  async canDeactivate() {
    const ok = await super.canDeactivate();
    if (!ok) return false;
    this.uow.clearEntities(ItemDetail);
    this.uow.clearEntities(Product);
    this.uow.clearEntities(ProductType);
    this.uow.clearEntities(ItemReplacement);
    return true;
  }

  async onIdsGridReady(event: GridReadyEvent) {
    const colDefs = [
      { ...UniAgFns.getManufacturerColDef("product.productType.manufacturer.name") },
      { ...UniAgFns.getProductTypeSKUColDef("product.productType.style") },
      { ...UniAgFns.getProductTypeDescriptionColDef("product.productType.description") },
      { ...UniAgFns.getFeatureChoicesColDef("product.featureChoicesExtract") },

      {
        headerName: "Account",
        field: "account.accountName",
        filter: "agTextColumnFilter",
      },

      {
        ...AgFns.createSetFilterObjectProps("In-Process Status", "itemDetailStatus.name", this.allItdStatuses),
      },
      { headerName: "Qty", field: "qty", ...AgFns.fixWidth(100) },
      { ...UniAgFns.getActiveStatusColDef("product.productType.activeStatus.name") },
    ];
    const sortModel = [
      { colId: "product.productType.manufacturer.name", sort: "asc" },
      { colId: "product.productType.description", sort: "asc" },
      { colId: "product.productType.style", sort: "asc" },
      // { colId: 'product.featureChoicesExtract', sort: 'asc' },
      // { colId: 'account.accountName', sort: 'asc' },
    ] as ISortModel;

    // next line is needed by the buildDatasource method
    AgFns.initGrid(event, colDefs, sortModel);
    this.updateDatasource();
    AgFns.applyGridRouteParams(this.idsGridOptions);
  }

  async onItdGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        headerName: "Item Detail",
        field: "id",
        cellRenderer: "agGroupCellRenderer",
        cellStyle: { textAlign: "left" },
        ...AgFns.fixWidth(150),
      },
      {
        headerName: "Item Bin",
        field: "itemBin.name",
        ...AgFns.flexWidth(150),
      },
      {
        headerName: "Account",
        field: "account.accountName",
        hide: !this.isAccountOwned,
        ...AgFns.fixWidth(150),
      },
      // { ...AgFns.createCellButtonProps("Mark", this.damageCell), ...AgFns.fixWidth(110) },
      {
        ...AgFns.createButtonProps("", this.markAction.bind(this), {
          calcLabel: this.showLabel.bind(this),
        }),
        colId: "mark",
        ...AgFns.fixWidth(150),
      },
      {
        headerName: "Job Order",
        ...NavFns.createIdCellClickedNavProps("joDetail.joHeaderId", this.router, "/in-process/manage/job-orders"),
        ...AgFns.fixWidth(150),
      },
      {
        headerName: "Purchase Order",
        ...NavFns.createIdCellClickedNavProps("poDetail.poHeaderId", this.router, "/purchasing/manage/purchase-orders"),
        ...AgFns.fixWidth(150),
      },
      {
        headerName: "Invoice",
        field: "invoiceHeaderId",
        ...AgFns.fixWidth(150),
      },
      {
        headerName: "Barcode Status",
        field: "barcodePrintStatus.name",
        ...AgFns.flexWidth(180),
      },
      { headerName: "Created", field: "crtnTs", filter: "agDateColumnFilter", ...AgFns.fixWidth(200) },
      {
        headerName: "Status",
        field: "itemDetailStatus.name",
        ...AgFns.flexWidth(150),
      },
    ];
    const sortModel = [
      { colId: "account.accountName", sort: "asc" },
      { colId: "id", sort: "asc" },
    ] as ISortModel;

    AgFns.initGrid(event, colDefs, sortModel);
  }

  markAction(row: ItemDetail) {
    if (this.canMarkDamaged(row)) {
      this.showDamaged(row);
    }
    if (this.canMarkUndamaged(row)) {
      this.markUndamaged(row);
    }
    if (this.canViewDamaged(row)) {
      this.showDamaged(row);
    }
  }

  showLabel(row: ItemDetail) {
    if (this.canViewDamaged(row)) {
      return "View Damaged";
    }

    if (this.canMarkDamaged(row)) {
      return "Mark Damaged";
    }

    if (this.canMarkUndamaged(row)) {
      return "Mark Undamaged";
    }
  }

  updateItdMasterDetail(parentGridOptions: GridOptions) {
    const detailGridOptions = AgFns.createDetailGridOptions();
    detailGridOptions.columnDefs = [
      { headerName: "Addon", field: "addon.nameAndLocation" },
      { headerName: "Addl. Info", field: "additionalInfo" },
      // { headerName: 'Orig Descr', field: 'addon.description' },
    ];
    AgFns.updateColDefs(detailGridOptions.columnDefs);
    parentGridOptions.detailCellRendererParams = {
      detailGridOptions: detailGridOptions,
      getDetailRowData: (params) => {
        const itd = params.data as ItemDetail;
        params.successCallback(itd.itemDetailAddons);
      },
    };
  }

  updateDatasource() {
    const gridApi = this.idsGridOptions.apiX;
    if (gridApi == null) {
      return;
    }

    const uniqSortModel = <ISortModel>[
      { colId: "productId", sort: "asc" },
      { colId: "itemDetailStatusId", sort: "asc" },
      { colId: "accountId", sort: "asc" },
    ];

    const opts = {
      initFn: () => {
        // clear bottom grid after change in top grid.
        this.itemDetails = [];
      },
    };
    const ds = AgFns.buildDatasource(() => this.dbQueryService.createItemDetailSummaryQuery(this.isAccountOwned), uniqSortModel, opts);

    gridApi.setGridOption("serverSideDatasource", ds);
    this.idsGridOptions.apiX.setColumnsVisible(["account.accountName"], this.isAccountOwned);
  }

  toggleIsAccountOwned() {
    this.updateDatasource();
  }

  onFilterChanged() {
    this.updateLocation();
  }

  updateLocation(key: any = null) {
    // Use current path instead of hardcoded path to support both standalone and managed routes
    const currentPath = this.domainService.location.path().split("?")[0];
    const urlTree = this.router.createUrlTree([currentPath]);
    const url = AgFns.buildGridRouteParamsUrl(urlTree, this.idsGridOptions, key && key.toString());
    this.domainService.location.replaceState(url);
  }

  async onIdsRowSelected(e: RowSelectedEvent) {
    // check if a deselect event and ignore
    if (!e.node.isSelected()) {
      return;
    }

    const ids = e.data as ItemDetailSummary;
    if (!ids) {
      return;
    }
    this.itdGridOptions.apiX.deselectAll(); //  to avoid Expression has changed after it was checked error
    this.updateLocation(this.getKeyForIds(ids));
    await this.getItemDetails(ids);
  }

  onItdRowSelected() {
    this.updateHasItdSelectedRows();
  }

  canMarkDamaged(itd: ItemDetail) {
    return itd.itemDetailStatusId !== ItemDetailStatusEnum.DamagedOrOther;
  }

  canViewDamaged(itd: ItemDetail) {
    return itd.itemDetailStatusId === ItemDetailStatusEnum.DamagedOrOther && itd.itemReplacement != null;
  }

  async showDamaged(itd: ItemDetail) {
    const r = await DialogBaseComponent.open(InventoryDamageDialogComponent, this, {
      itemDetail: itd,
    });
    if (r) {
      this.updateDatasource();
    }
    return <boolean>r;
  }

  canMarkUndamaged(itd: ItemDetail) {
    return itd.itemDetailStatusId === ItemDetailStatusEnum.DamagedOrOther;
  }

  @wrapBusy
  async markUndamaged(itd: ItemDetail) {
    itd.itemDetailStatusId = ItemDetailStatusEnum.InInventory;

    // also need to remove corresponding itemReplacement item.
    if (itd.itemReplacement != null) {
      itd.itemReplacement.entityAspect.setDeleted();
    }
    await this.dbSaveService.saveChanges();
    // Reset selection state before updating datasource to avoid ExpressionChangedAfterItHasBeenCheckedError
    this.hasItdSelectedRows = false;
    this.inventoryManager?.updatePrintBarcodesEnabled(false);
    this.updateDatasource();
    // Reload item details for the currently selected row in the top grid
    const selectedRow = this.idsGridOptions.apiX?.getSelectedRows()?.[0] as ItemDetailSummary;
    if (selectedRow) {
      await this.getItemDetails(selectedRow);
    }
  }

  async getItemDetails(ids: ItemDetailSummary) {
    this.itemDetails = await AgFns.busyGrid(this.itdGridOptions, this, () => {
      return this.dbQueryService.getItemDetailsByItemDetailSummary(ids);
    });
    return this.itemDetails;
  }

  updateHasItdSelectedRows() {
    const rows = this.itdGridOptions.apiX?.getSelectedRows();
    this.hasItdSelectedRows = rows && rows.length > 0;
    // Update parent menu's Print Barcodes enabled state
    this.inventoryManager?.updatePrintBarcodesEnabled(this.hasItdSelectedRows);
  }

  /** Print ZPL labels for all the selected rows */
  async printBarcodes() {
    const itemDetails = this.itdGridOptions.apiX.getSelectedRows() as ItemDetail[];
    await this.barcodeService.printItemDetailBarcodes(itemDetails);

    const ok = await this.dialogService.yesNoDialog("Barcode Printing", `Did ${itemDetails.length} barcode(s) print correctly?`);
    if (ok) {
      itemDetails.forEach((itd) => (itd.barcodePrintStatusId = BarcodePrintStatusEnum.PrintedForStock));
      await this.dbSaveService.saveChanges();
      this.itdGridOptions.apiX.redrawRows();
      this.toastr.success(`Barcode print status updated for ${itemDetails.length} items`);
    }
  }
}
